import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, Hotel, Home, Backpack } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { getCity } from "@/lib/city";
import { SearchBar } from "@/components/SearchBar";
import { SpotCard } from "@/components/SpotCard";
import { FilterSelect } from "@/components/FilterSelect";
import { ExplorerMap } from "@/components/ExplorerMap";
import { useLanguage } from "@/i18n/LanguageContext";

const TYPE_CHIPS = [
  { key: "tous", label: "Tous", icon: LayoutGrid },
  { key: "hotel", label: "Hôtels", icon: Hotel },
  { key: "auberge", label: "Auberges", icon: Home },
  { key: "auberge-jeunesse", label: "Auberges de jeunesse", icon: Backpack },
];

export default function HebergementsPage() {
  const [spots, setSpots] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("tous");
  const [prix, setPrix] = useState("tous");
  const [note, setNote] = useState("toutes");
  const [quartier, setQuartier] = useState("tous");
  const [sort, setSort] = useState("proximite");
  const [view, setView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024 ? "liste" : "carte"
  );
  const { t, lang } = useLanguage();

  useEffect(() => {
    Promise.all([api.get("/establishments", { params: { category: "hebergements" } }), api.get("/cities")])
      .then(([s, v]) => {
        setSpots(s.data);
        setCities(v.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const neighborhoods = useMemo(() => [...new Set(spots.map((s) => s.neighborhood))].sort(), [spots]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = spots.filter(
      (s) =>
        (type === "tous" || s.lodge_type === type) &&
        (!q || s.name.toLowerCase().includes(q) || s.neighborhood.toLowerCase().includes(q)) &&
        (prix === "tous" || (s.price_from || 0) <= Number(prix)) &&
        (note === "toutes" || s.rating >= Number(note)) &&
        (quartier === "tous" || s.neighborhood === quartier)
    );
    return list.sort((a, b) => {
      if (sort === "prix") return (a.price_from || 9999) - (b.price_from || 9999);
      if (sort === "note") return b.rating - a.rating;
      return a.distance_m - b.distance_m;
    });
  }, [spots, query, type, prix, note, quartier, sort]);

  const cityName = getCity().split(",")[0];

  return (
    <main data-testid="hebergements-page" className="mx-auto max-w-[1800px] px-4 pb-16 pt-8 sm:px-6 sm:pt-10 lg:px-10">
      <Seo
        title={t("Hébergements à Québec — GoSpot")}
        siteName="GoSpot"
        description={t("Hôtels, auberges et hébergements uniques à Québec, avec prix par nuit et carte.")}
      />

      <motion.h1
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
      >
        {lang === "en" ? `Stays in ${cityName}` : `Hébergements à ${cityName}`}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.1 }}
        className="mt-2 text-base text-slate-600"
      >
        {lang === "en"
          ? `Hotels, inns and unique stays for your next night in ${cityName}.`
          : `Hôtels, auberges et hébergements uniques pour ta prochaine nuit à ${cityName}.`}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.18 }}
        className="mt-6 max-w-2xl"
      >
        <SearchBar cities={cities} initialQuery={query} onSearch={(val) => setQuery(val)} placeholder={t("Rechercher un hôtel, une auberge…")} />
      </motion.div>

      {/* TYPES */}
      <div className="no-scrollbar mt-6 flex snap-x gap-2.5 overflow-x-auto pb-1">
        {TYPE_CHIPS.map((chip) => {
          const Icon = chip.icon;
          const active = type === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              data-testid={`lodge-chip-${chip.key}`}
              onClick={() => setType(chip.key)}
              className={`flex shrink-0 snap-start items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition duration-300 ${
                active
                  ? "border-[#2563EB] bg-[#2563EB] text-white shadow-[0_6px_18px_rgba(37,99,235,0.25)]"
                  : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-200"
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? "text-white" : "text-slate-500"}`} />
              {t(chip.label)}
            </button>
          );
        })}
      </div>

      {/* FILTRES */}
      <div className="no-scrollbar mt-4 flex snap-x gap-2.5 overflow-x-auto pb-1">
        <FilterSelect
          label="Prix par nuit"
          value={prix}
          onChange={setPrix}
          testId="lodge-filter-prix"
          options={[
            { value: "tous", label: "Tous les prix" },
            { value: "150", label: "150 $ et moins" },
            { value: "300", label: "300 $ et moins" },
          ]}
        />
        <FilterSelect
          label="Note"
          value={note}
          onChange={setNote}
          testId="lodge-filter-note"
          options={[
            { value: "toutes", label: "Toutes les notes" },
            { value: "4", label: "4,0 et plus" },
            { value: "4.5", label: "4,5 et plus" },
          ]}
        />
        <FilterSelect
          label="Quartier"
          value={quartier}
          onChange={setQuartier}
          testId="lodge-filter-quartier"
          options={[
            { value: "tous", label: "Tous les quartiers" },
            ...neighborhoods.map((n) => ({ value: n, label: n })),
          ]}
        />
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <p data-testid="lodge-results-count" className="text-sm font-bold text-slate-900">
          {lang === "en"
            ? `${filtered.length} ${filtered.length > 1 ? "stays" : "stay"} in ${cityName}`
            : `${filtered.length} hébergement${filtered.length > 1 ? "s" : ""} à ${cityName}`}
        </p>
        <label className="flex shrink-0 items-center gap-1.5 text-sm text-slate-500">
          <span className="hidden sm:inline">{t("Trier par :")}</span>
          <select
            data-testid="lodge-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="cursor-pointer bg-transparent font-semibold text-[#2563EB] outline-none"
          >
            <option value="proximite">{t("proximité")}</option>
            <option value="prix">{t("prix")}</option>
            <option value="note">{t("note")}</option>
          </select>
        </label>
      </div>

      <div className="mt-4 flex items-start gap-6">
        <div
          className={
            view === "carte"
              ? "hidden flex-1 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid xl:grid-cols-3"
              : "grid flex-1 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          }
        >
          {loading ? (
            [0, 1, 2, 3].map((i) => <div key={i} className="h-[340px] animate-pulse rounded-3xl bg-white" />)
          ) : filtered.length === 0 ? (
            <div data-testid="lodge-no-results" className="col-span-full rounded-3xl border border-slate-100 bg-white p-10 text-center">
              <p className="font-semibold text-slate-900">{t("Aucun hébergement trouvé")}</p>
              <p className="mt-1 text-sm text-slate-500">{t("Essaie d’ajuster tes filtres.")}</p>
            </div>
          ) : (
            filtered.map((spot) => <SpotCard key={spot.slug} spot={spot} imageClassName="h-56" />)
          )}
        </div>

        {view === "carte" && (
          <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[380px] xl:w-[420px]">
            <ExplorerMap markers={filtered} view={view} onViewChange={setView} />
          </aside>
        )}
      </div>

      {view === "liste" && (
        <button
          type="button"
          data-testid="lodge-show-map-fab"
          onClick={() => setView("carte")}
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white shadow-xl transition hover:bg-[#1D4ED8]"
        >
          {t("Voir la carte")}
        </button>
      )}
    </main>
  );
}
